// src/components/ServiceRecovery/ManagerAlertsInbox.js
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, ChevronRight, Clock, FileText, Package, UserCheck, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import userService from '../../services/userService';
import { typography } from '../../styles/typography';
import { Card } from '../ui';
import serviceRecoveryApi from './serviceRecoveryApi';

// Dummy data for development/testing - COMMENTED OUT
// const getDummyAlerts = () => {
//   const now = new Date();
//   return [
//     {
//       alert_id: 'alert-001',
//       id: 'alert-001',
//       order_id: 'ORD-2024-001234',
//       guest_name: 'John Smith',
//       feedback_category: 'Food',
//       sentiment_score: 0.25,
//       feedback_text: 'The food was cold and took too long to arrive. Very disappointed.',
//       created_at: new Date(now.getTime() - 5 * 60000).toISOString(), // 5 minutes ago
//       timestamp: new Date(now.getTime() - 5 * 60000).toISOString(),
//       severity: 'high',
//     },
//     {
//       alert_id: 'alert-002',
//       id: 'alert-002',
//       order_id: 'ORD-2024-001189',
//       guest_name: 'Sarah Johnson',
//       feedback_category: 'Service',
//       sentiment_score: 0.35,
//       feedback_text: 'Waiter was rude and inattentive. Had to wait 20 minutes for the bill.',
//       created_at: new Date(now.getTime() - 15 * 60000).toISOString(), // 15 minutes ago
//       timestamp: new Date(now.getTime() - 15 * 60000).toISOString(),
//       severity: 'high',
//     },
//     {
//       alert_id: 'alert-003',
//       id: 'alert-003',
//       order_id: 'ORD-2024-000987',
//       guest_name: 'Michael Chen',
//       feedback_category: 'Cleanliness',
//       sentiment_score: 0.42,
//       feedback_text: 'Table was sticky and there were crumbs on the floor.',
//       created_at: new Date(now.getTime() - 30 * 60000).toISOString(), // 30 minutes ago
//       timestamp: new Date(now.getTime() - 30 * 60000).toISOString(),
//       severity: 'medium',
//     },
//     {
//       alert_id: 'alert-004',
//       id: 'alert-004',
//       order_id: 'ORD-2024-001456',
//       guest_name: 'Emily Davis',
//       feedback_category: 'Food',
//       sentiment_score: 0.28,
//       feedback_text: 'Order was wrong and food quality was poor. Expected better.',
//       created_at: new Date(now.getTime() - 45 * 60000).toISOString(), // 45 minutes ago
//       timestamp: new Date(now.getTime() - 45 * 60000).toISOString(),
//       severity: 'high',
//     },
//     {
//       alert_id: 'alert-005',
//       id: 'alert-005',
//       order_id: 'ORD-2024-001321',
//       guest_name: 'Robert Wilson',
//       feedback_category: 'Service',
//       sentiment_score: 0.38,
//       feedback_text: 'Service was slow and staff seemed overwhelmed.',
//       created_at: new Date(now.getTime() - 60 * 60000).toISOString(), // 1 hour ago
//       timestamp: new Date(now.getTime() - 60 * 60000).toISOString(),
//       severity: 'medium',
//     },
//     {
//       alert_id: 'alert-006',
//       id: 'alert-006',
//       order_id: 'ORD-2024-000876',
//       guest_name: 'Lisa Anderson',
//       feedback_category: 'Food',
//       sentiment_score: 0.31,
//       feedback_text: 'Food was overcooked and lacked flavor. Not worth the price.',
//       created_at: new Date(now.getTime() - 90 * 60000).toISOString(), // 1.5 hours ago
//       timestamp: new Date(now.getTime() - 90 * 60000).toISOString(),
//       severity: 'high',
//     },
//     {
//       alert_id: 'alert-007',
//       id: 'alert-007',
//       order_id: 'ORD-2024-001567',
//       guest_name: 'David Martinez',
//       feedback_category: 'Cleanliness',
//       sentiment_score: 0.45,
//       feedback_text: 'Restroom was not clean and needed maintenance.',
//       created_at: new Date(now.getTime() - 120 * 60000).toISOString(), // 2 hours ago
//       timestamp: new Date(now.getTime() - 120 * 60000).toISOString(),
//       severity: 'medium',
//     },
//     {
//       alert_id: 'alert-008',
//       id: 'alert-008',
//       order_id: 'ORD-2024-001098',
//       guest_name: 'Jennifer Brown',
//       feedback_category: 'Service',
//       sentiment_score: 0.22,
//       feedback_text: 'Terrible experience. Staff ignored our requests multiple times.',
//       created_at: new Date(now.getTime() - 150 * 60000).toISOString(), // 2.5 hours ago
//       timestamp: new Date(now.getTime() - 150 * 60000).toISOString(),
//       severity: 'high',
//     },
//   ];
// };

// // Generate dummy guest history
// const getDummyGuestHistory = (orderId) => {
//   return {
//     guest_id: `guest-${orderId.split('-')[2]}`,
//     total_visits: Math.floor(Math.random() * 10) + 1,
//     prior_issues: [
//       {
//         date: '2024-01-15',
//         description: 'Previous complaint about slow service',
//         resolved: true,
//       },
//       {
//         date: '2024-01-10',
//         description: 'Food quality issue reported',
//         resolved: true,
//       },
//     ],
//     average_sentiment: 0.65,
//     preferred_category: 'Food',
//   };
// };

const ManagerAlertsInbox = () => {
  // Log screen view
  useScreenLogging('ManagerAlertsInbox', {
    component: 'ManagerAlertsInbox',
    screenType: 'manager-alerts',
  });

  const { openOverlay, closeOverlay } = useOverlay();
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [guestHistory, setGuestHistory] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignSuccess, setAssignSuccess] = useState(null);
  const [recoverySuccess, setRecoverySuccess] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  // const [useDummyData, setUseDummyData] = useState(false); // COMMENTED OUT

  // Fetch permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const userPermissions = await userService.getUserPermissions();
        setPermissions(userPermissions || []);
      } catch (error) {
        console.error('Error fetching permissions:', error);
        setPermissions([]);
      } finally {
        setIsLoadingPermissions(false);
      }
    };
    fetchPermissions();
  }, []);

  useEffect(() => {
    fetchAlerts();
    // Poll for new alerts every 30 seconds
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      setIsLoading(true);
      const response = await serviceRecoveryApi.getManagerAlerts(50);
      const alertsData = response.alerts || response || [];
      
      // Normalize alerts: extract metadata fields to top level for easier access
      const normalizedAlerts = alertsData.map((alert, index) => {
        // Log first alert structure for debugging
        if (index === 0) {
          console.log('📡 ManagerAlertsInbox: Sample alert structure:', {
            alert,
            allKeys: Object.keys(alert),
            metadata: alert.metadata,
            metadataKeys: alert.metadata ? Object.keys(alert.metadata) : null,
          });
        }
        
        if (alert.metadata && typeof alert.metadata === 'object') {
          return {
            ...alert,
            order_id: alert.order_id || alert.metadata.order_id,
            feedback_category: alert.feedback_category || alert.metadata.feedback_category,
            feedback_text: alert.feedback_text || alert.metadata.feedback_text || alert.message,
            sentiment_score: alert.sentiment_score !== undefined ? alert.sentiment_score : (alert.metadata.sentiment_score !== undefined ? alert.metadata.sentiment_score : 0),
            recovery_action_taken: alert.recovery_action_taken || alert.metadata.recovery_action_taken,
            site_id: alert.site_id || alert.metadata.site_id,
            guest_name: alert.guest_name || alert.metadata.guest_name || alert.metadata.guestName || alert.guestName,
            assigned_to: alert.assigned_to || alert.metadata.assigned_to || alert.metadata.assigned_staff_id,
            assigned_staff_id: alert.assigned_staff_id || alert.metadata.assigned_staff_id || alert.metadata.assigned_to,
            assigned_to_name: alert.assigned_to_name || alert.metadata.assigned_to_name || alert.metadata.assigned_staff_name,
            assigned_staff_name: alert.assigned_staff_name || alert.metadata.assigned_staff_name || alert.metadata.assigned_to_name,
          };
        }
        return alert;
      });
      
      console.log('📡 ManagerAlertsInbox: Normalized alerts:', normalizedAlerts);
      setAlerts(normalizedAlerts);
      setError(null);
      
      // Log API event for fetching manager alerts
      loggingService.logInfo(
        'manager_alerts_fetch',
        `Fetched ${normalizedAlerts.length} manager alerts`,
        {
          endpoint: '/alerts',
          method: 'GET',
          alertCount: normalizedAlerts.length,
          limit: 50,
        }
      ).catch(logError => {
        console.warn('[ManagerAlertsInbox] Failed to log event:', logError);
      });
      // if (alertsData.length > 0) {
      //   setAlerts(alertsData);
      //   setUseDummyData(false);
      //   setError(null);
      // } else {
      //   // Use dummy data if API returns empty array - COMMENTED OUT
      //   setAlerts(getDummyAlerts());
      //   setUseDummyData(true);
      // }
    } catch (err) {
      console.error('Error fetching alerts:', err);
      // Provide more specific error message for CORS errors
      if (err.name === 'CORS_ERROR' || err.message?.includes('CORS')) {
        setError(
          'CORS Error: Unable to connect to the server. ' +
          'This is a server configuration issue. Please contact the backend team to enable CORS for this origin.'
        );
      } else {
        setError(err.message || 'Failed to fetch alerts. Please try again.');
      }
      // Use dummy data on error - COMMENTED OUT
      // setAlerts(getDummyAlerts());
      // setUseDummyData(true);
      // setError('Using dummy data for development');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenGuestProfile = async (alert) => {
    console.log('📡 ManagerAlertsInbox: Opening alert details for alert:', alert);
    console.log('📡 ManagerAlertsInbox: Alert keys:', Object.keys(alert));
    console.log('📡 ManagerAlertsInbox: Alert metadata:', alert.metadata);
    console.log('📡 ManagerAlertsInbox: Guest name fields:', {
      guest_name: alert.guest_name,
      'metadata.guest_name': alert.metadata?.guest_name,
      'metadata.guestName': alert.metadata?.guestName,
      guestName: alert.guestName,
    });
    setSelectedAlert(alert);
    setIsLoadingHistory(true);
    setGuestHistory(null); // Reset previous history
    try {
      const orderId = alert.order_id || alert.metadata?.order_id || alert.id;
      console.log('📡 ManagerAlertsInbox: Extracted order_id:', orderId, 'from alert:', alert);
      
      // If no order ID, use alert data to create basic guest history
      if (!orderId) {
        console.warn('⚠️ ManagerAlertsInbox: No order_id found, using alert data for guest history');
        setGuestHistory({
          total_visits: 1,
          average_sentiment: alert.sentiment_score !== undefined ? alert.sentiment_score : 0,
          preferred_category: alert.feedback_category || alert.category || 'N/A',
          prior_issues: [],
        });
        setIsLoadingHistory(false);
        return;
      }
      
      const feedbackId = alert.feedback_id || alert.metadata?.feedback_id;
      
      // First, try to get order details to fetch guest name
      let guestNameFromOrder = null;
      try {
        const orderDetails = await serviceRecoveryApi.getFeedbackByOrderId(orderId);
        if (orderDetails) {
          guestNameFromOrder = orderDetails.customer_name || orderDetails.customerName || 
                             orderDetails.guest_name || orderDetails.guestName ||
                             (orderDetails.customer && (orderDetails.customer.name || orderDetails.customer.customer_name)) ||
                             (orderDetails.guest && (orderDetails.guest.name || orderDetails.guest.guest_name));
          if (guestNameFromOrder) {
            setSelectedAlert(prev => ({
              ...prev,
              guest_name: guestNameFromOrder,
            }));
          }
          
          // Log API event for fetching feedback by order ID
          loggingService.logInfo(
            'feedback_by_order_fetch',
            `Fetched feedback details for order ${orderId}`,
            {
              endpoint: `/guest-feedback/order/${orderId}`,
              method: 'GET',
              orderId: orderId,
              hasGuestName: !!guestNameFromOrder,
            }
          ).catch(logError => {
            console.warn('[ManagerAlertsInbox] Failed to log event:', logError);
          });
        }
      } catch (orderError) {
        // Order API optional; we'll use guest profile or alert data
      }
      
      // Guest profile: try order ID first, then feedback_id if backend expects it
      // let history = await serviceRecoveryApi.getGuestProfile(orderId);
      // if (history == null && feedbackId) {
      //   history = await serviceRecoveryApi.getGuestProfile(feedbackId);
      // }
      
      // Helper: use alert data when guest profile is not found (404) or API fails
      const setHistoryFromAlert = () => {
        setGuestHistory({
          total_visits: 1,
          average_sentiment: alert.sentiment_score !== undefined ? alert.sentiment_score : 0,
          preferred_category: alert.feedback_category || alert.category || 'N/A',
          prior_issues: [],
        });
      };

      try {
        // Handle different response structures (history is null when profile not found / 404)
        if (history && typeof history === 'object') {
          // Check if it's a single feedback object (API returns feedback instead of profile)
          if (history.feedback_id && history.order_id && !history.total_visits) {
            const mergedAlert = {
              ...alert,
              order_id: history.order_id || alert?.order_id || alert?.metadata?.order_id,
              feedback_category: history.feedback_category || alert?.feedback_category || alert?.metadata?.feedback_category,
              feedback_text: history.feedback_text || alert?.feedback_text || alert?.message,
              sentiment_score: history.sentiment_score !== undefined ? history.sentiment_score : (alert?.sentiment_score !== undefined ? alert.sentiment_score : 0),
              recovery_action_taken: history.recovery_action_taken || alert?.recovery_action_taken || alert?.metadata?.recovery_action_taken,
              feedback_id: history.feedback_id,
              site_id: history.site_id || alert?.site_id || alert?.metadata?.site_id,
              guest_name: guestNameFromOrder || history.guest_name || history.guestName || history.customer_name || history.customerName || alert?.guest_name || alert?.metadata?.guest_name || alert?.metadata?.guestName,
            };
            setSelectedAlert(mergedAlert);
            setGuestHistory({
              total_visits: 1,
              average_sentiment: history.sentiment_score || 0,
              preferred_category: history.feedback_category || 'N/A',
              prior_issues: [],
              current_feedback: history,
            });
          } else if (history.profile || history.guest || history.data) {
            const profileData = history.profile || history.guest || history.data;
            setGuestHistory(profileData);
          } else {
            setGuestHistory(history);
          }
        } else {
          // No guest profile (404) or empty response – use alert data; optionally retry order for guest name
          if (!guestNameFromOrder) {
            try {
              const orderDetails = await serviceRecoveryApi.getFeedbackByOrderId(orderId);
              if (orderDetails) {
                const name = orderDetails.customer_name || orderDetails.customerName ||
                  orderDetails.guest_name || orderDetails.guestName ||
                  (orderDetails.customer && (orderDetails.customer.name || orderDetails.customer.customer_name)) ||
                  (orderDetails.guest && (orderDetails.guest.name || orderDetails.guest.guest_name));
                if (name) setSelectedAlert(prev => ({ ...prev, guest_name: name }));
                
                // Log API event for fetching feedback by order ID
                loggingService.logInfo(
                  'feedback_by_order_fetch',
                  `Fetched feedback details for order ${orderId} (retry)`,
                  {
                    endpoint: `/guest-feedback/order/${orderId}`,
                    method: 'GET',
                    orderId: orderId,
                    hasGuestName: !!name,
                  }
                ).catch(logError => {
                  console.warn('[ManagerAlertsInbox] Failed to log event:', logError);
                });
              }
            } catch (_) { /* ignore */ }
          }
          setHistoryFromAlert();
        }
      } catch (profileError) {
        console.warn('⚠️ ManagerAlertsInbox: Guest profile API failed, using alert data:', profileError);
        if (!guestNameFromOrder) {
          try {
            const orderDetails = await serviceRecoveryApi.getFeedbackByOrderId(orderId);
            if (orderDetails) {
              const name = orderDetails.customer_name || orderDetails.customerName ||
                orderDetails.guest_name || orderDetails.guestName ||
                (orderDetails.customer && (orderDetails.customer.name || orderDetails.customer.customer_name)) ||
                (orderDetails.guest && (orderDetails.guest.name || orderDetails.guest.guest_name));
              if (name) setSelectedAlert(prev => ({ ...prev, guest_name: name }));
              
              // Log API event for fetching feedback by order ID
              loggingService.logInfo(
                'feedback_by_order_fetch',
                `Fetched feedback details for order ${orderId} (error fallback)`,
                {
                  endpoint: `/guest-feedback/order/${orderId}`,
                  method: 'GET',
                  orderId: orderId,
                  hasGuestName: !!name,
                }
              ).catch(logError => {
                console.warn('[ManagerAlertsInbox] Failed to log event:', logError);
              });
            }
          } catch (_) { /* ignore */ }
        }
        setHistoryFromAlert();
      }
    } catch (err) {
      console.error('❌ ManagerAlertsInbox: Error in handleOpenGuestProfile:', err);
      // Even on error, show basic info from alert
      setGuestHistory({
        total_visits: 1,
        average_sentiment: alert.sentiment_score !== undefined ? alert.sentiment_score : 0,
        preferred_category: alert.feedback_category || alert.category || 'N/A',
        prior_issues: [],
        error: err.message || 'Failed to fetch guest history',
      });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleAssignToStaff = async (alertId, staffId) => {
    setIsAssigning(true);
    setAssignSuccess(null);
    try {
      const response = await serviceRecoveryApi.assignAlertToStaff(alertId, staffId);
      console.log('✅ Assign to staff successful:', response);
      
      // Log API event for assigning alert to staff
      loggingService.logInfo(
        'alert_assign_staff',
        `Assigned alert ${alertId} to staff member ${staffId}`,
        {
          endpoint: `/alerts/${alertId}/assign`,
          method: 'PUT',
          alertId: alertId,
          staffId: staffId,
        }
      ).catch(logError => {
        console.warn('[ManagerAlertsInbox] Failed to log event:', logError);
      });
      
      // Show success message
      setAssignSuccess(`Alert successfully assigned to staff member (ID: ${staffId})`);
      
      // Refresh alerts to show updated assignment status
      await fetchAlerts();
      
      // Clear success message after 3 seconds and close modal
      setTimeout(() => {
        setAssignSuccess(null);
        setSelectedAlert(null);
      }, 3000);
    } catch (err) {
      console.error('❌ Error assigning alert:', err);
      const msg = err?.message || 'Failed to assign alert to staff';
      const is401 = err?.status === 401;
      const isTenantRelated = /tenant\s*(id\s*)?missing|missing\s*tenant|tenant_id|tenant\s*context/i.test(msg);
      const userMsg =
        is401 && isTenantRelated
          ? 'Assign failed: The API requires your Cognito user to have a tenant (custom:tenant_id). Ask your administrator to add the custom attribute "custom:tenant_id" to your user in Cognito and set it to your tenant ID (e.g. 0), then sign out and sign in again.'
          : is401
            ? `Assign failed: ${msg}. You may not have permission or your account may be missing tenant context.`
            : `Failed to assign alert: ${msg}`;
      alert(userMsg);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleLogRecoveryAction = async (alertId, recoveryData) => {
    setRecoverySuccess(null);
    try {
      const response = await serviceRecoveryApi.logRecoveryAction(alertId, recoveryData);
      console.log('✅ Recovery action logged successfully:', response);
      
      // Log API event for logging recovery action
      loggingService.logInfo(
        'recovery_action_log',
        `Logged recovery action for alert ${alertId}`,
        {
          endpoint: `/guest-feedback/recovery/${alertId}`,
          method: 'POST',
          alertId: alertId,
          hasRecoveryAction: !!recoveryData.recovery_action_taken,
          hasSentimentChange: recoveryData.guest_sentiment_change_index !== undefined,
        }
      ).catch(logError => {
        console.warn('[ManagerAlertsInbox] Failed to log event:', logError);
      });
      
      // Show success message
      setRecoverySuccess('Recovery action logged successfully. Alert marked as recovered.');
      
      // Refresh alerts to show updated recovery status
      await fetchAlerts();
      
      // Clear success message after 3 seconds and close modal
      setTimeout(() => {
        setRecoverySuccess(null);
        setSelectedAlert(null);
      }, 3000);
    } catch (err) {
      console.error('Error logging recovery action:', err);
      const msg = err?.message || 'Failed to log recovery action';
      const is401 = err?.status === 401;
      const isTenantRelated = /tenant\s*(id\s*)?missing|missing\s*tenant|tenant_id|tenant\s*context/i.test(msg);
      const isDynamoDBError = /UpdateExpression|ValidationException|SET SET/i.test(msg);
      
      let userMsg;
      if (isDynamoDBError) {
        userMsg = 'Recovery action failed: Backend database error. The backend Lambda function has an issue with the DynamoDB UpdateExpression. Please contact the backend team to fix the UpdateExpression syntax (see RECOVERY_ACTION_DYNAMODB_FIX.md for details).';
      } else if (is401 && isTenantRelated) {
        userMsg = 'Recovery action failed: The API requires your Cognito user to have a tenant (custom:tenant_id). Ask your administrator to add the custom attribute "custom:tenant_id" in Cognito and set it to your tenant ID, then sign out and sign in again.';
      } else if (is401) {
        userMsg = `Recovery action failed: ${msg}. You may not have permission or your account may be missing tenant context.`;
      } else {
        userMsg = `Failed to log recovery action: ${msg}`;
      }
      alert(userMsg);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getSeverityColor = (sentimentScore) => {
    const severity = serviceRecoveryApi.getSentimentSeverity(sentimentScore);
    return severity.color;
  };

  const getSeverityLabel = (sentimentScore) => {
    const severity = serviceRecoveryApi.getSentimentSeverity(sentimentScore);
    return severity.label;
  };

  if (isLoading && alerts.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className={typography.p} style={{ color: '#000000' }}>Loading alerts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className={typography.h2} style={{ color: '#000000' }}>
          Manager Alerts
        </h2>
        <div className="flex gap-3">
          {/* {useDummyData && (
            <span className="px-3 py-2 bg-yellow-100 text-yellow-800 rounded-lg text-sm flex items-center">
              Using Dummy Data
            </span>
          )} */}
          <button
            onClick={fetchAlerts}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <Card.Light className="p-4 border-red-300 bg-red-50">
          <p className="text-red-700">{error}</p>
        </Card.Light>
      )}

      {alerts.length === 0 ? (
        <Card.Gradient className="p-8 text-center">
          <p className={typography.p} style={{ color: '#000000' }}>
            No alerts at this time
          </p>
        </Card.Gradient>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <motion.div
              key={alert.alert_id || alert.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card.Gradient className="p-4 hover:shadow-xl transition-shadow cursor-pointer">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <AlertTriangle
                        className="w-5 h-5"
                        style={{ color: getSeverityColor(alert.sentiment_score || 0.5) }}
                      />
                      <span
                        className="px-2 py-1 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: getSeverityColor(alert.sentiment_score || 0.5) + '20',
                          color: getSeverityColor(alert.sentiment_score || 0.5),
                        }}
                      >
                        {getSeverityLabel(alert.sentiment_score || 0.5)}
                      </span>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(alert.created_at || alert.timestamp)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-700">Order:</span>
                        <span className="font-semibold">
                          {alert.order_id || alert.metadata?.order_id || (alert.metadata && typeof alert.metadata === 'object' && alert.metadata.order_id) || alert.id || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-700">Category: </span>
                        <span className="font-semibold capitalize">
                          {alert.feedback_category || alert.category || alert.metadata?.feedback_category || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-700">Sentiment: </span>
                        <span className="font-semibold">
                          {(alert.sentiment_score !== undefined && alert.sentiment_score !== null
                            ? alert.sentiment_score
                            : 0).toFixed(2)}
                        </span>
                      </div>
                      {(alert.assigned_to || alert.assigned_staff_id) && (
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-4 h-4 text-blue-600" />
                          <span className="text-gray-700">Assigned: </span>
                          <span className="font-semibold text-blue-600">
                            {alert.assigned_to_name || alert.assigned_staff_name || 'Yes'}
                          </span>
                        </div>
                      )}
                      {alert.recovery_action_taken && (
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-green-600" />
                          <span className="text-gray-700">Status: </span>
                          <span className="font-semibold text-green-600">Recovered</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => handleOpenGuestProfile(alert)}
                    className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </Card.Gradient>
            </motion.div>
          ))}
        </div>
      )}

      {/* Detail Drawer - portaled to body so it displays on the viewport above layout */}
      {selectedAlert &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
              style={{ zIndex: 9999 }}
              onClick={() => setSelectedAlert(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
              <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
                <h3 className={typography.h3} style={{ color: '#000000' }}>
                  Alert Details
                </h3>
                <button
                  onClick={() => {
                    setSelectedAlert(null);
                    setGuestHistory(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Success Messages */}
                {assignSuccess && (
                  <Card.Light className="p-4 bg-green-50 border-green-200">
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-5 h-5 text-green-600" />
                      <p className="text-green-800 font-semibold">{assignSuccess}</p>
                    </div>
                  </Card.Light>
                )}
                {recoverySuccess && (
                  <Card.Light className="p-4 bg-green-50 border-green-200">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      <p className="text-green-800 font-semibold">{recoverySuccess}</p>
                    </div>
                  </Card.Light>
                )}

                {/* Alert Info */}
                <Card.Light className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-600">Guest Name:</span>
                      <p className="font-semibold">{selectedAlert.guest_name || selectedAlert.metadata?.guest_name || selectedAlert.metadata?.guestName || selectedAlert.guestName || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Order ID:</span>
                      <p className="font-semibold">{selectedAlert.order_id || selectedAlert.id || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Category:</span>
                      <p className="font-semibold capitalize">{selectedAlert.feedback_category || selectedAlert.category || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Sentiment Score:</span>
                      <p className="font-semibold">
                        {(selectedAlert.sentiment_score || 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Time:</span>
                      <p className="font-semibold">
                        {formatTime(selectedAlert.created_at || selectedAlert.timestamp)}
                      </p>
                    </div>
                    {(selectedAlert.assigned_to || selectedAlert.assigned_staff_id) && (
                      <div className="col-span-2">
                        <span className="text-gray-600">Assigned To:</span>
                        <p className="font-semibold text-blue-600">
                          {selectedAlert.assigned_to_name || selectedAlert.assigned_staff_name || `Staff ID: ${selectedAlert.assigned_to || selectedAlert.assigned_staff_id}`}
                        </p>
                      </div>
                    )}
                    {selectedAlert.recovery_action_taken && (
                      <div className="col-span-2">
                        <span className="text-gray-600">Status:</span>
                        <p className="font-semibold text-green-600 flex items-center gap-2">
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm">✓ Recovered</span>
                        </p>
                        <div className="mt-2">
                          <span className="text-gray-600 text-sm">Recovery Action:</span>
                          <p className="text-sm font-medium mt-1">{selectedAlert.recovery_action_taken}</p>
                        </div>
                      </div>
                    )}
                    {/* {selectedAlert.recovery_action_taken && (
                      <div className="col-span-2">
                        <span className="text-gray-600">Status:</span>
                        <p className="font-semibold text-green-600 flex items-center gap-2">
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm">✓ Recovered</span>
                        </p>
                        <div className="mt-2">
                          <span className="text-gray-600 text-sm">Recovery Action:</span>
                          <p className="text-sm font-medium mt-1">{selectedAlert.recovery_action_taken}</p>
                        </div>
                      </div>
                    )} */}
                    {selectedAlert.feedback_text && (
                      <div className="col-span-2">
                        <span className="text-gray-600">Feedback:</span>
                        <p className="font-semibold">{selectedAlert.feedback_text}</p>
                      </div>
                    )}
                  </div>
                </Card.Light>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  {/* Check if feedback is positive, already assigned, or recovered - disable button accordingly */}
                  {(() => {
                    const sentimentScore = selectedAlert.sentiment_score !== undefined && selectedAlert.sentiment_score !== null
                      ? selectedAlert.sentiment_score
                      : 0;
                    const isPositive = sentimentScore >= 0.7;
                    const isAssigned = selectedAlert.assigned_to || selectedAlert.assigned_staff_id;
                    const isRecovered = !!selectedAlert.recovery_action_taken;
                    const isDisabled = isPositive || isAssigned || isRecovered || isAssigning;
                    
                    if (isAssigned) {
                      return (
                        <div className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-600 rounded-lg cursor-not-allowed">
                          <UserCheck className="w-4 h-4" />
                          <span>
                            Already Assigned to: {selectedAlert.assigned_to_name || selectedAlert.assigned_staff_name || `Staff ID: ${selectedAlert.assigned_to || selectedAlert.assigned_staff_id}`}
                          </span>
                        </div>
                      );
                    }
                    
                    if (isRecovered) {
                      return (
                        <div className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-600 rounded-lg cursor-not-allowed">
                          <UserCheck className="w-4 h-4" />
                          <span>Cannot assign - Alert already recovered</span>
                        </div>
                      );
                    }
                    
                    return (
                      <button
                        onClick={() => {
                          const alertId = selectedAlert.alert_id || selectedAlert.id;
                          openOverlay('selectEmployeeForAlert', {
                            alertId,
                            onSelect: (employee) => {
                              closeOverlay('selectEmployeeForAlert');
                              handleAssignToStaff(alertId, employee.employee_id || employee.user_id);
                            },
                          });
                        }}
                        disabled={isDisabled}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          isDisabled
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {isAssigning ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Assigning...
                          </>
                        ) : (
                          <>
                            <UserCheck className="w-4 h-4" />
                            Assign to Staff
                          </>
                        )}
                      </button>
                    );
                  })()}
                  
                  {/* Log Recovery Action button - disabled for positive feedback or if already recovered */}
                  {(() => {
                    const sentimentScore = selectedAlert.sentiment_score !== undefined && selectedAlert.sentiment_score !== null
                      ? selectedAlert.sentiment_score
                      : 0;
                    const isPositive = sentimentScore >= 0.7;
                    const isRecovered = !!selectedAlert.recovery_action_taken;
                    const isDisabled = isPositive || isRecovered;
                    
                    if (isRecovered) {
                      return (
                        <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg cursor-not-allowed">
                          <FileText className="w-4 h-4" />
                          <span>Already Recovered</span>
                        </div>
                      );
                    }
                    
                    return (
                      <button
                        onClick={() => {
                          const recovery_action_taken = prompt('Recovery action taken (e.g. Offered complimentary dessert):');
                          const sentimentChange = prompt('Guest sentiment change index (0 to 1, e.g. 0.3):');
                          const recovery_notes = prompt('Recovery notes (optional):');
                          if (recovery_action_taken != null && recovery_action_taken.trim() && sentimentChange != null && sentimentChange.trim()) {
                            const feedbackId = selectedAlert.feedback_id || selectedAlert.alert_id || selectedAlert.id;
                            handleLogRecoveryAction(feedbackId, {
                              recovery_action_taken: recovery_action_taken.trim(),
                              guest_sentiment_change_index: parseFloat(sentimentChange) || 0,
                              recovery_notes: recovery_notes ? recovery_notes.trim() : '',
                            });
                          }
                        }}
                        disabled={isDisabled}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          isDisabled
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        <FileText className="w-4 h-4" />
                        Log Recovery Action
                      </button>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default ManagerAlertsInbox;
